/* eslint-disable react/no-multi-comp */
import React, { useState, useRef, useEffect, useContext, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import {
  Search,
  Send,
  Check,
  CheckCheck,
  ArrowLeft,
  Loader2,
  X,
  Paperclip,
  Mic,
  Square,
  ChevronUp,
  ChevronDown,
  Plus,
  Users,
  Info,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { authContext } from 'contexts/AuthContext';
import { axiosPost } from 'services';
import { playOutgoingMessageTone } from 'utils/staffChatAudio';
import {
  GET_STAFF_CHAT_USERS,
  GET_STAFF_CHAT_MESSAGES,
  SEND_STAFF_MESSAGE,
  MARK_STAFF_CHAT_MESSAGES_AS_SEEN,
  GET_UNREAD_COUNT,
  GET_STAFF_CHAT_GROUPS,
  CREATE_STAFF_CHAT_GROUP,
  GET_STAFF_CHAT_GROUP_MESSAGES,
  SEND_STAFF_CHAT_GROUP_MESSAGE,
} from './StaffChatGraphQL';
import './StaffChat.scss';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/* ─── Helpers ─────────────────────────────────────────────────────── */
const getAvatarLetters = (firstName, lastName) => {
  if (!firstName && !lastName) return '??';
  const first = (firstName || '').trim();
  const last = (lastName || '').trim();
  if (first && last) return (first[0] + last[0]).toUpperCase();
  if (first) return first.substring(0, 2).toUpperCase();
  if (last) return last.substring(0, 2).toUpperCase();
  return '??';
};

const getFullName = (firstName, middleName, lastName) => {
  const parts = [firstName, middleName, lastName].filter(Boolean);
  return parts.join(' ') || 'Unknown User';
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const formatContactTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (msgDate.getTime() === today.getTime()) {
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }
  if (msgDate.getTime() === yesterday.getTime()) return 'Yesterday';

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${date.getDate()} ${months[date.getMonth()]}`;
};

const formatDateSeparator = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (msgDate.getTime() === today.getTime()) return 'Today';
  if (msgDate.getTime() === yesterday.getTime()) return 'Yesterday';
  return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
};

const AVATAR_COLORS = [
  '#25D366', '#128C7E', '#075E54', '#34B7F1',
  '#00BCD4', '#9C27B0', '#FF5722', '#607D8B',
  '#E91E63', '#3F51B5', '#FF9800', '#4CAF50',
];

const getAvatarColor = (str) => {
  let hash = 0;
  const text = String(str || '');
  for (let i = 0; i < text.length; i += 1)
    hash = text.charCodeAt(i) + (Math.imul(hash, 32) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getMemberDetails = (member, currentUserId, auth, localContacts) => {
  const memberId = String(member.userId || member.id);
  const isMe = memberId === String(currentUserId);
  const matched = localContacts.find((c) => String(c.id) === memberId);

  let fullName = `Staff #${memberId}`;
  if (isMe) {
    const myName = auth?.data?.firstName
      ? getFullName(auth?.data?.firstName, auth?.data?.middleName, auth?.data?.lastName)
      : 'You';
    fullName = `${myName} (You)`;
  } else if (matched) {
    fullName = getFullName(matched.firstName, matched.middleName, matched.lastName);
  } else if (member.firstName) {
    fullName = getFullName(member.firstName, member.middleName, member.lastName);
  }

  const phone = matched?.phone || member.phone || '';

  let letters = '??';
  if (isMe) {
    letters = getAvatarLetters(auth?.data?.firstName, auth?.data?.lastName);
  } else if (matched) {
    letters = getAvatarLetters(matched.firstName, matched.lastName);
  } else if (member.firstName) {
    letters = getAvatarLetters(member.firstName, member.lastName);
  }

  return { memberId, isMe, fullName, phone, letters, color: getAvatarColor(memberId) };
};

/* ─── HighlightText ─────────────────────────────────────────────────── */
const HighlightText = ({ text, term }) => {
  if (!term || !term.trim() || !text) return text;
  const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) => {
        if (regex.test(part)) {
          // eslint-disable-next-line react/no-array-index-key
          return <mark key={i} className="search-highlight">{part}</mark>;
        }
        // eslint-disable-next-line react/no-array-index-key
        return <span key={i}>{part}</span>;
      })}
    </>
  );
};

const MessageStatusIcon = ({ isSeen, isSent }) => {
  if (!isSent) return null;
  if (isSeen) return <CheckCheck size={16} color="#53bdeb" />;
  return <Check size={16} color="#858B8F" />;
};

const MessageBubble = ({ msg, currentUserId, highlightTerm, isActiveMatch, isGroup }) => {
  const isSent = String(msg.senderId) === String(currentUserId);
  const rowClass = `message-row ${isSent ? 'sent' : 'received'}${isActiveMatch ? ' active-search-match' : ''}`;
  const bubbleClass = `message-bubble ${isSent ? 'sent-bubble' : 'received-bubble'}`;

  // WhatsApp-style system message (e.g. group created, participant added)
  if (msg.messageType === 'SYSTEM') {
    return (
      <div className="group-system-row">
        <span className="group-system-badge">{msg.message}</span>
      </div>
    );
  }

  // Sender name above received message in group chats (like WhatsApp)
  const renderSenderName = () => {
    if (!isGroup || isSent) return null;
    return (
      <span
        className="message-sender-name"
        style={{ color: getAvatarColor(msg.senderId || msg.senderName) }}
      >
        {msg.senderName || 'Staff Member'}
      </span>
    );
  };

  const renderBubbleContent = () => {
    if (msg.messageType === 'IMAGE' && msg.mediaUrl) {
      return (
        <div className="message-bubble message-bubble--media">
          {renderSenderName()}
          <a
            className="message-image-link"
            href={msg.mediaUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open image"
          >
            <img
              className="message-image"
              src={msg.mediaUrl}
              alt="Shared media"
              loading="lazy"
            />
          </a>
          <div className="message-meta message-meta--overlay">
            <span className="message-time">{formatTime(msg.createdAt)}</span>
            <span className="message-status">
              <MessageStatusIcon isSeen={msg.isSeen} isSent={isSent} />
            </span>
          </div>
        </div>
      );
    }

    if (msg.messageType === 'VIDEO' && msg.mediaUrl) {
      return (
        <div className="message-bubble message-bubble--media">
          {renderSenderName()}
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            className="message-video"
            src={msg.mediaUrl}
            controls
            style={{ width: 'min(260px, 60vw)', maxHeight: '300px', borderRadius: '10px', objectFit: 'cover' }}
          />
          <div className="message-meta" style={{ position: 'static', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
            <span className="message-time" style={{ color: '#858B8F' }}>{formatTime(msg.createdAt)}</span>
            <span className="message-status">
              <MessageStatusIcon isSeen={msg.isSeen} isSent={isSent} />
            </span>
          </div>
        </div>
      );
    }

    if (msg.messageType === 'AUDIO' && msg.mediaUrl) {
      return (
        <div className={bubbleClass} style={{ display: 'flex', flexDirection: 'column', minWidth: '220px' }}>
          {renderSenderName()}
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio controls src={msg.mediaUrl} className="message-audio" style={{ maxWidth: '100%', height: '40px' }} />
          <div className="message-meta" style={{ position: 'static', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
            <span className="message-time">{formatTime(msg.createdAt)}</span>
            <span className="message-status">
              <MessageStatusIcon isSeen={msg.isSeen} isSent={isSent} />
            </span>
          </div>
        </div>
      );
    }

    if (msg.messageType === 'DOCUMENT' && msg.mediaUrl) {
      return (
        <div className={bubbleClass} style={{ minWidth: '160px', display: 'flex', flexDirection: 'column' }}>
          {renderSenderName()}
          <a
            href={msg.mediaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="message-document-link"
            style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#111b21', textDecoration: 'none', wordBreak: 'break-word', paddingBottom: '4px' }}
          >
            <Paperclip size={16} color="#111b21" style={{ flexShrink: 0 }} />
            <span style={{ fontSize: '13px', color: '#111b21', fontWeight: 500 }}>{msg.message || 'View Document'}</span>
          </a>
          <div
            className="message-meta"
            style={{ position: 'static', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '4px', marginTop: '2px' }}
          >
            <span className="message-time" style={{ color: '#667781' }}>{formatTime(msg.createdAt)}</span>
            <span className="message-status">
              <MessageStatusIcon isSeen={msg.isSeen} isSent={isSent} />
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className={bubbleClass}>
        {renderSenderName()}
        <span className="message-text">
          <HighlightText text={msg.message} term={highlightTerm} />
        </span>
        <div className="message-meta">
          <span className="message-time">{formatTime(msg.createdAt)}</span>
          <span className="message-status">
            <MessageStatusIcon isSeen={msg.isSeen} isSent={isSent} />
          </span>
        </div>
      </div>
    );
  };

  return (
    <div id={`chat-msg-${msg.id}`} className={rowClass} data-sender={msg.senderId} data-current={currentUserId}>
      {renderBubbleContent()}
    </div>
  );
};

const MessagesContent = ({ messages, currentUserId, highlightTerm, activeMatchId, isGroup }) => {
  if (messages.length === 0) {
    return (
      <div className="chat-no-messages">
        <p>{isGroup ? 'No messages in this group yet. Send a message to start conversation!' : 'No messages yet. Start a conversation!'}</p>
      </div>
    );
  }

  const messagesWithSeparators = [];
  let lastDateLabel = null;

  messages.forEach((msg, idx) => {
    const dateLabel = formatDateSeparator(msg.createdAt);

    if (dateLabel && dateLabel !== lastDateLabel) {
      messagesWithSeparators.push({
        type: 'date-separator',
        id: `date-${idx}`,
        label: dateLabel,
      });
      lastDateLabel = dateLabel;
    }

    messagesWithSeparators.push({ type: 'message', data: msg });
  });

  return messagesWithSeparators.map((item) => {
    if (item.type === 'date-separator') {
      return (
        <div key={item.id} className="date-separator">
          <span className="date-separator-label">{item.label}</span>
        </div>
      );
    }
    return (
      <MessageBubble
        key={item.data.id}
        msg={item.data}
        currentUserId={currentUserId}
        highlightTerm={highlightTerm}
        isActiveMatch={activeMatchId && String(item.data.id) === String(activeMatchId)}
        isGroup={isGroup}
      />
    );
  });
};

/* ─── Main StaffChat Component ──────────────────────────────────────── */
const StaffChat = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeStaffId } = useParams();
  const { auth } = useContext(authContext);
  const token = auth?.data?.token;
  const currentUserId = auth?.data?.userId ?? auth?.data?.id ?? auth?.data?.user_id ?? auth?.data?.user?.id;

  const [mobileView, setMobileView] = useState('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeContact, setActiveContact] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [localMessages, setLocalMessages] = useState([]);
  const [localContacts, setLocalContacts] = useState([]);

  // Tab State: 'all' | 'direct' | 'groups'
  const [activeTab, setActiveTab] = useState('all');

  // Group State (strictly loaded from backend GraphQL API)
  const [groups, setGroups] = useState([]);

  // Create Group Modal State
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');

  // Group Info Modal State
  const [isGroupInfoOpen, setIsGroupInfoOpen] = useState(false);

  // Message Search State
  const [isMessageSearchOpen, setIsMessageSearchOpen] = useState(false);
  const [messageSearchTerm, setMessageSearchTerm] = useState('');

  // File Upload State
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  // Audio Recording State
  const [isRecordingAudio, setIsRecordingAudio] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // WebSocket state
  const [, setWsConnected] = useState(false);
  const stompClientRef = useRef(null);
  const chatSubRef = useRef(null);
  const activeContactRef = useRef(null);

  const messagesEndRef = useRef(null);
  const chatWindowRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastLoadedGroupIdRef = useRef(null);
  const lastLoadedUserIdRef = useRef(null);

  // Fetch staff chat users
  const { data: usersData, loading: loadingUsers, error: usersError, refetch: refetchUsers } = useQuery(
    GET_STAFF_CHAT_USERS,
    {
      variables: {
        filter: [
          {
            key: 'search',
            value: searchQuery,
            type: 'STRING',
            op: 'LIKE',
          },
        ],
        pageNo: 0,
        pageSize: 20,
        sortCol: '',
        sortDirection: 'ASC',
        pagination: true,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  // Lazy query for 1-to-1 messages
  const [loadMessages, { data: messagesData, loading: loadingMessages }] = useLazyQuery(
    GET_STAFF_CHAT_MESSAGES,
    {
      fetchPolicy: 'network-only',
    },
  );

  // Lazy query for group messages
  const [loadGroupMessages, {
    data: groupMessagesData,
    loading: loadingGroupMessages,
  }] = useLazyQuery(GET_STAFF_CHAT_GROUP_MESSAGES, {
    fetchPolicy: 'network-only',
  });

  // Mutation to mark messages as seen
  const [markMessagesAsSeen] = useMutation(MARK_STAFF_CHAT_MESSAGES_AS_SEEN, {
    refetchQueries: [{ query: GET_UNREAD_COUNT }],
    onCompleted: () => {
      refetchUsers();
    },
    onError: (error) => {
      console.error('Mark as seen error:', error);
    }
  });

  // Send message mutation (1-to-1)
  const [sendMessage, { loading: sendingMessage }] = useMutation(SEND_STAFF_MESSAGE, {
    onCompleted: (data) => {
      setMessageText('');
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      if (activeContact && !activeContact.isGroup && data?.sendStaffInternalMessage) {
        const newMsg = data.sendStaffInternalMessage;
        setLocalMessages((prev) => {
          if (prev.find((m) => m.id === newMsg.id)) return prev;
          return [...prev, newMsg];
        });

        // Update sidebar instantly
        setLocalContacts(prevContacts => {
          const updatedContacts = [...prevContacts];
          const contactIndex = updatedContacts.findIndex(c => String(c.id) === String(activeContact.id));
          if (contactIndex > -1) {
            const contact = updatedContacts[contactIndex];
            const updatedContact = {
              ...contact,
              lastMessage: newMsg.message,
              lastMessageAt: newMsg.createdAt,
              lastMessageType: newMsg.messageType,
            };
            updatedContacts.splice(contactIndex, 1);
            updatedContacts.unshift(updatedContact);
          }
          return updatedContacts;
        });
      }
      refetchUsers();
    },
    onError: (error) => {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      toast.error(error.message || 'Failed to send message');
      console.error('Send message error:', error);
    },
  });

  // Send group message mutation
  const [sendStaffChatGroupMessage, { loading: sendingGroupMessage }] = useMutation(
    SEND_STAFF_CHAT_GROUP_MESSAGE,
    {
      onCompleted: (data) => {
        setMessageText('');
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
        const newMsgData = data?.sendStaffChatGroupMessage;
        if (activeContact?.isGroup && newMsgData) {
          const myName = getFullName(auth?.data?.firstName, auth?.data?.middleName, auth?.data?.lastName) || 'You';
          const newMsg = {
            id: String(newMsgData.messageId),
            messageId: String(newMsgData.messageId),
            groupId: String(newMsgData.groupId),
            senderId: String(newMsgData.senderId),
            senderName: myName,
            message: newMsgData.message,
            messageType: newMsgData.messageType || 'TEXT',
            mediaUrl: newMsgData.mediaUrl || null,
            createdAt: newMsgData.createdAt || new Date().toISOString(),
            isSeen: true,
          };

          setLocalMessages((prev) => {
            if (prev.find((m) => String(m.id || m.messageId) === String(newMsg.id))) return prev;
            return [...prev, newMsg];
          });

          // Update sidebar instantly
          setGroups((prevGroups) => {
            const updated = [...prevGroups];
            const targetGroupId = String(newMsg.groupId);
            const idx = updated.findIndex((g) => String(g.id) === targetGroupId || String(g.groupId) === targetGroupId);
            if (idx > -1) {
              const grp = {
                ...updated[idx],
                lastMessage: newMsg.message,
                lastMessageAt: newMsg.createdAt,
                lastMessageType: newMsg.messageType,
              };
              updated.splice(idx, 1);
              updated.unshift(grp);
            }
            return updated;
          });
        }
      },
      onError: (error) => {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
        toast.error(error.message || 'Failed to send group message');
        console.error('Send group message error:', error);
      },
    }
  );

  // Fetch staff chat groups (strictly from backend GraphQL API)
  const {
    data: groupsData,
    loading: loadingGroups,
    error: groupsError,
    refetch: refetchGroups,
  } = useQuery(GET_STAFF_CHAT_GROUPS, {
    fetchPolicy: 'cache-and-network',
  });

  // Create staff chat group mutation
  const [createStaffChatGroup, { loading: creatingGroup }] = useMutation(
    CREATE_STAFF_CHAT_GROUP,
    {
      onCompleted: (data) => {
        const created = data?.createStaffChatGroup;
        if (created) {
          const newGroup = {
            ...created,
            id: String(created.groupId),
            groupId: String(created.groupId),
            isGroup: true,
            memberIds: created.members?.map((m) => String(m.userId)) || [],
          };

          const initialMsg = {
            id: `grp-sys-${Date.now()}`,
            groupId: newGroup.id,
            message: `You created group "${newGroup.name}"`,
            messageType: 'SYSTEM',
            createdAt: created.createdAt || new Date().toISOString(),
          };

          setNewGroupName('');
          setNewGroupDescription('');
          setSelectedMemberIds([]);
          setMemberSearchQuery('');
          setIsCreateGroupModalOpen(false);

          setActiveContact(newGroup);
          if (String(routeStaffId) !== String(newGroup.id)) {
            navigate(`/staff-chat/${newGroup.id}`);
          }
          setMobileView('chat');
          setIsMessageSearchOpen(false);
          setMessageSearchTerm('');
          setLocalMessages([initialMsg]);

          toast.success(`Group "${newGroup.name}" created successfully!`);
          refetchGroups();
        }
      },
      onError: (error) => {
        console.error('Create group error:', error);
        toast.error(error.message || 'Failed to create group');
      },
    }
  );

  // Sync localContacts when usersData changes
  useEffect(() => {
    if (usersData?.staffChatUsers) {
      setLocalContacts(usersData.staffChatUsers);
    }
  }, [usersData]);

  // Sync groups when groupsData changes (strictly from backend, deduplicated by groupId)
  useEffect(() => {
    if (groupsData?.getStaffChatGroups) {
      const seenGroupIds = new Set();
      const mappedGroups = [];
      groupsData.getStaffChatGroups.forEach((g) => {
        const gid = String(g.groupId);
        if (!seenGroupIds.has(gid)) {
          seenGroupIds.add(gid);
          mappedGroups.push({
            ...g,
            id: gid,
            groupId: gid,
            isGroup: true,
            memberIds: g.members?.map((m) => String(m.userId)) || [],
          });
        }
      });
      setGroups(mappedGroups);

      // Keep active group in sync if currently selected
      if (activeContactRef.current?.isGroup) {
        const currentActiveId = String(activeContactRef.current.groupId || activeContactRef.current.id);
        const updated = mappedGroups.find(
          (g) => String(g.id) === currentActiveId || String(g.groupId) === currentActiveId
        );
        if (
          updated &&
          (updated.name !== activeContactRef.current.name ||
            updated.description !== activeContactRef.current.description ||
            updated.memberCount !== activeContactRef.current.memberCount)
        ) {
          setActiveContact((prev) => ({ ...prev, ...updated }));
        }
      }
    }
  }, [groupsData]);

  // Sync localMessages when messagesData changes (only for 1-to-1 chats)
  useEffect(() => {
    if (!activeContact?.isGroup && messagesData?.staffChatMessages) {
      setLocalMessages(messagesData.staffChatMessages);
    }
  }, [messagesData, activeContact]);

  // Sync localMessages when groupMessagesData changes (for group chats)
  useEffect(() => {
    if (activeContact?.isGroup && groupMessagesData?.getStaffChatGroupMessages?.content) {
      const activeGroupId = String(activeContact.groupId || activeContact.id);
      const rawContent = groupMessagesData.getStaffChatGroupMessages.content;

      const formatted = rawContent
        .filter((m) => String(m.groupId) === activeGroupId)
        .map((m) => {
          const mSenderId = String(m.senderId);
          const isSentByMe = mSenderId === String(currentUserId);
          let senderName = `Staff #${mSenderId}`;
          if (isSentByMe) {
            senderName = 'You';
          } else {
            const foundUser = localContacts.find((c) => String(c.id) === mSenderId);
            if (foundUser) {
              senderName = getFullName(foundUser.firstName, foundUser.middleName, foundUser.lastName);
            }
          }

          return {
            id: String(m.messageId),
            messageId: String(m.messageId),
            groupId: String(m.groupId),
            senderId: mSenderId,
            senderName,
            message: m.message,
            messageType: m.messageType || 'TEXT',
            mediaUrl: m.mediaUrl || null,
            createdAt: m.createdAt,
            seenCount: m.seenCount,
            totalMembers: m.totalMembers,
            isSeen: (m.seenCount || 0) > 1 || isSentByMe,
          };
        });

      // Sort ascending by createdAt (oldest first, newest at bottom)
      formatted.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

      setLocalMessages(formatted);
    }
  }, [groupMessagesData, activeContact, currentUserId, localContacts]);

  // Load messages once when activeContact changes (e.g. via direct URL route or selection)
  useEffect(() => {
    if (activeContact?.isGroup) {
      const gId = String(activeContact.groupId || activeContact.id);
      if (lastLoadedGroupIdRef.current !== gId) {
        lastLoadedGroupIdRef.current = gId;
        lastLoadedUserIdRef.current = null;
        loadGroupMessages({
          variables: {
            groupId: gId,
            page: 0,
            size: 50,
          },
        });
      }
    } else if (activeContact && !activeContact.isGroup) {
      const uId = String(activeContact.id);
      if (lastLoadedUserIdRef.current !== uId) {
        lastLoadedUserIdRef.current = uId;
        lastLoadedGroupIdRef.current = null;
        loadMessages({
          variables: {
            staffId: uId,
            searchTerm: null,
          },
        });
      }
    }
  }, [activeContact, loadGroupMessages, loadMessages]);

  // Keep activeContactRef in sync for WebSocket callbacks
  useEffect(() => {
    activeContactRef.current = activeContact;
  }, [activeContact]);

  // Cleanup any legacy saved groups or messages from localStorage once on mount
  useEffect(() => {
    try {
      localStorage.removeItem('polystox_staff_groups');
      localStorage.removeItem('polystox_staff_group_messages');
    } catch (e) {
      // ignore
    }
  }, []);

  /* ═══ WEBSOCKET SETUP ═══════════════════════════════════════════════ */
  useEffect(() => {
    if (!token || !currentUserId) return undefined;

    const client = new Client({
      webSocketFactory: () => new SockJS(`${API_BASE}/ws-endpoint`),
      connectHeaders: { Authorization: `Bearer ${token}` },
      reconnectDelay: 5000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,

      onConnect: () => {
        setWsConnected(true);
        // eslint-disable-next-line no-console
        console.log('✅ StaffChat WebSocket Connected');

        // Subscribe to staff chat updates for this user
        client.subscribe(`/topic/staff-chat/${currentUserId}`, (frame) => {
          try {
            const payload = JSON.parse(frame.body);
            // eslint-disable-next-line no-console
            console.log('📨 WebSocket Message Received:', payload);

            // Check if this is a group message
            if (payload.groupId) {
              const incomingGroupId = String(payload.groupId);
              const newGroupMsg = {
                id: payload.id || `ws-grp-${Date.now()}`,
                groupId: incomingGroupId,
                senderId: String(payload.senderId || ''),
                senderName: payload.senderName || 'Staff Member',
                message: payload.message || '',
                messageType: payload.messageType || 'TEXT',
                mediaUrl: payload.mediaUrl || null,
                createdAt: payload.createdAt || new Date().toISOString(),
              };

              setGroups((prev) => {
                const updated = [...prev];
                const idx = updated.findIndex((g) => String(g.id) === incomingGroupId || String(g.groupId) === incomingGroupId);
                if (idx > -1) {
                  const grp = {
                    ...updated[idx],
                    lastMessage: payload.message,
                    lastMessageAt: payload.createdAt || new Date().toISOString(),
                    lastMessageType: payload.messageType || 'TEXT',
                  };
                  const currentActiveId = String(activeContactRef.current?.groupId || activeContactRef.current?.id);
                  if (currentActiveId !== incomingGroupId) {
                    grp.unreadCount = (grp.unreadCount || 0) + 1;
                  }
                  updated.splice(idx, 1);
                  updated.unshift(grp);
                }
                return updated;
              });

              const currentActiveId = String(activeContactRef.current?.groupId || activeContactRef.current?.id);
              if (currentActiveId === incomingGroupId) {
                setLocalMessages((prev) => {
                  if (prev.find((m) => m.id === newGroupMsg.id)) return prev;
                  return [...prev, newGroupMsg];
                });
              }
              return;
            }

            // Standard 1-to-1 direct message
            const newMessage = {
              id: payload.id || `ws-${Date.now()}`,
              senderId: String(payload.senderId || ''),
              receiverId: String(payload.receiverId || ''),
              message: payload.message || '',
              messageType: payload.messageType || 'TEXT',
              mediaUrl: payload.mediaUrl || null,
              isSeen: payload.isSeen || false,
              createdAt: payload.createdAt || new Date().toISOString(),
            };

            const senderId = String(payload.senderId);
            const isSentByMe = senderId === String(currentUserId);

            setLocalContacts((prevContacts) => {
              const updatedContacts = [...prevContacts];
              const targetContactId = isSentByMe ? String(payload.receiverId) : senderId;

              const contactIndex = updatedContacts.findIndex((c) => String(c.id) === targetContactId);

              if (contactIndex > -1) {
                const contact = updatedContacts[contactIndex];
                const updatedContact = {
                  ...contact,
                  lastMessage: payload.message,
                  lastMessageAt: payload.createdAt || new Date().toISOString(),
                  lastMessageType: payload.messageType || 'TEXT',
                };

                if (!isSentByMe && String(activeContactRef.current?.id) !== String(contact.id)) {
                  updatedContact.unreadCount = (updatedContact.unreadCount || 0) + 1;
                }

                updatedContacts.splice(contactIndex, 1);
                updatedContacts.unshift(updatedContact);
              }
              return updatedContacts;
            });

            refetchUsers();

            if (activeContactRef.current && !activeContactRef.current.isGroup) {
              const activeId = String(activeContactRef.current.id);
              if (senderId === activeId || String(payload.receiverId) === activeId) {
                setLocalMessages((prev) => {
                  if (prev.find((m) => m.id === newMessage.id)) return prev;
                  return [...prev, newMessage];
                });

                if (senderId === activeId) {
                  markMessagesAsSeen({
                    variables: {
                      staffId: activeContactRef.current.id,
                    },
                  });
                }
              }
            }
          } catch (err) {
            console.error('❌ WebSocket parse error:', err);
          }
        });

        stompClientRef.current = client;
      },

      onDisconnect: () => {
        setWsConnected(false);
      },

      onStompError: (frame) => {
        console.error('❌ STOMP error:', frame);
        setWsConnected(false);
      },
    });

    client.activate();
    stompClientRef.current = client;

    return () => {
      if (chatSubRef.current) {
        try {
          chatSubRef.current.unsubscribe();
        } catch (err) {
          // ignore
        }
        chatSubRef.current = null;
      }
      client.deactivate();
      stompClientRef.current = null;
      setWsConnected(false);
    };
  }, [token, currentUserId, refetchUsers, loadMessages, markMessagesAsSeen]);

  // Auto-focus input when active chat changes
  useEffect(() => {
    if (activeContact) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [activeContact]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  }, [localMessages]);

  // Client-side message search
  const matchingMessageIds = useMemo(() => {
    if (!messageSearchTerm.trim()) return [];
    const term = messageSearchTerm.toLowerCase().trim();
    return localMessages
      .filter((m) => m.messageType === 'TEXT' && m.message && m.message.toLowerCase().includes(term))
      .map((m) => String(m.id));
  }, [localMessages, messageSearchTerm]);

  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const messageSearchInputRef = useRef(null);

  useEffect(() => {
    setCurrentMatchIndex(0);
  }, [messageSearchTerm]);

  useEffect(() => {
    if (matchingMessageIds.length > 0 && chatWindowRef.current) {
      const matchId = matchingMessageIds[currentMatchIndex];
      const el = document.getElementById(`chat-msg-${matchId}`);
      if (el) {
        const container = chatWindowRef.current;
        const containerRect = container.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const relativeTop = elRect.top - containerRect.top + container.scrollTop;
        container.scrollTo({
          top: relativeTop - container.clientHeight / 2,
          behavior: 'smooth',
        });
      }
    }
  }, [currentMatchIndex, matchingMessageIds]);

  useEffect(() => {
    if (isMessageSearchOpen && messageSearchInputRef.current) {
      setTimeout(() => messageSearchInputRef.current?.focus(), 50);
    }
  }, [isMessageSearchOpen]);

  const handleNextMatch = () => {
    if (matchingMessageIds.length === 0) return;
    setCurrentMatchIndex((prev) => (prev + 1) % matchingMessageIds.length);
  };

  const handlePrevMatch = () => {
    if (matchingMessageIds.length === 0) return;
    setCurrentMatchIndex((prev) => (prev - 1 + matchingMessageIds.length) % matchingMessageIds.length);
  };

  const handleCloseSearch = () => {
    setIsMessageSearchOpen(false);
    setMessageSearchTerm('');
    setCurrentMatchIndex(0);
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
  };

  // Combined List: Supports WhatsApp-style tabs ("All", "Direct", "Groups")
  const combinedChatList = useMemo(() => {
    const searchLower = searchQuery.toLowerCase().trim();

    const directList = localContacts
      .map((c) => ({ ...c, isGroup: false }))
      .filter((c) => {
        if (!searchLower) return true;
        const name = getFullName(c.firstName, c.middleName, c.lastName).toLowerCase();
        const phone = (c.phone || '').toLowerCase();
        return name.includes(searchLower) || phone.includes(searchLower);
      });

    const seenGroupIds = new Set();
    const groupList = [];
    groups.forEach((g) => {
      const gid = String(g.groupId || g.id);
      if (!seenGroupIds.has(gid)) {
        seenGroupIds.add(gid);
        const name = (g.name || '').toLowerCase();
        const desc = (g.description || '').toLowerCase();
        const matchesSearch = !searchLower || name.includes(searchLower) || desc.includes(searchLower);
        if (matchesSearch) {
          groupList.push({ ...g, isGroup: true, id: gid, groupId: gid });
        }
      }
    });

    if (activeTab === 'direct') return directList;
    if (activeTab === 'groups') return groupList;

    // 'all' tab: merge & sort by last message time
    return [...groupList, ...directList].sort((a, b) => {
      const timeA = (a.lastMessageAt || a.createdAt) ? new Date(a.lastMessageAt || a.createdAt).getTime() : 0;
      const timeB = (b.lastMessageAt || b.createdAt) ? new Date(b.lastMessageAt || b.createdAt).getTime() : 0;
      return timeB - timeA;
    });
  }, [localContacts, groups, searchQuery, activeTab]);

  const handleContactClick = (item) => {
    setActiveContact(item);
    const targetId = String(item.groupId || item.id);
    if (String(routeStaffId) !== targetId) {
      navigate(`/staff-chat/${targetId}`);
    }
    setMobileView('chat');
    setIsMessageSearchOpen(false);
    setMessageSearchTerm('');

    if (item.isGroup) {
      lastLoadedGroupIdRef.current = targetId;
      lastLoadedUserIdRef.current = null;
      loadGroupMessages({
        variables: {
          groupId: targetId,
          page: 0,
          size: 50,
        },
      });

      // Reset group unread count
      if ((item.unreadCount || 0) > 0) {
        setGroups((prev) => {
          const updated = prev.map((g) => (
            (String(g.id) === targetId || String(g.groupId) === targetId)
              ? { ...g, unreadCount: 0 }
              : g
          ));
          return updated;
        });
      }
    } else {
      // 1-to-1 chat: load via GraphQL
      lastLoadedUserIdRef.current = String(item.id);
      lastLoadedGroupIdRef.current = null;
      loadMessages({
        variables: {
          staffId: item.id,
          searchTerm: null,
        },
      });

      if ((item.unreadCount || 0) > 0) {
        markMessagesAsSeen({
          variables: {
            staffId: item.id,
          },
        });
        setLocalContacts((prev) => prev.map((c) => (
          String(c.id) === String(item.id) ? { ...c, unreadCount: 0 } : c
        )));
      }
    }
  };

  // Auto-select contact/group when URL has /staff-chat/:id
  useEffect(() => {
    if (routeStaffId) {
      // First check if it's a group
      const foundGroup = groups.find(
        (g) => String(g.id) === String(routeStaffId) || String(g.groupId) === String(routeStaffId)
      );
      if (foundGroup) {
        if (
          String(activeContact?.id) !== String(foundGroup.id) &&
          String(activeContact?.groupId) !== String(foundGroup.groupId)
        ) {
          handleContactClick(foundGroup);
        }
        return;
      }

      // Check direct contacts
      if (localContacts.length > 0) {
        const found = localContacts.find((c) => String(c.id) === String(routeStaffId));
        if (found) {
          if (String(activeContact?.id) !== String(found.id)) {
            handleContactClick(found);
          }
        } else if (!loadingUsers && (!activeContact || String(activeContact.id) !== String(routeStaffId))) {
          const fallbackContact = {
            id: routeStaffId,
            firstName: 'Staff',
            lastName: `#${routeStaffId}`,
          };
          handleContactClick(fallbackContact);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeStaffId, localContacts, groups, loadingUsers]);

  const handleBackToList = () => {
    setMobileView('list');
    navigate('/staff-chat');
  };

  const handleBackToStaff = () => {
    const from = location.state?.from || '/staff';
    navigate(from);
  };

  const handleToggleMember = (contactId) => {
    const idStr = String(contactId);
    setSelectedMemberIds((prev) => {
      if (prev.includes(idStr)) {
        return prev.filter((id) => id !== idStr);
      }
      return [...prev, idStr];
    });
  };

  /* ═══ GROUP CREATION HANDLER ════════════════════════════════════════ */
  const handleCreateGroupSubmit = async () => {
    const trimmedName = newGroupName.trim();
    if (!trimmedName) {
      toast.error('Please enter a group subject / name');
      return;
    }

    const memberIds = selectedMemberIds
      .map(String)
      .filter((id) => id !== String(currentUserId));

    if (memberIds.length === 0) {
      toast.error('Please select at least one staff member to add');
      return;
    }

    const input = {
      name: trimmedName,
      memberIds,
    };

    const trimmedDesc = newGroupDescription?.trim();
    if (trimmedDesc) {
      input.description = trimmedDesc;
    }

    try {
      await createStaffChatGroup({
        variables: { input },
      });
    } catch (err) {
      // Handled in onError
    }
  };

  /* ═══ SEND GROUP MESSAGE HELPER ════════════════════════════════════ */
  const handleSendGroupMessage = (text, type = 'TEXT', mediaUrl = null) => {
    if (!activeContact?.isGroup || sendingGroupMessage) return;

    const activeGroupId = String(activeContact.groupId || activeContact.id);

    sendStaffChatGroupMessage({
      variables: {
        input: {
          groupId: activeGroupId,
          message: text,
          messageType: type,
          mediaUrl: mediaUrl || null,
        },
      },
    });

    playOutgoingMessageTone();
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  /* ═══ GENERAL SEND MESSAGE (DISPATCHES DIRECT OR GROUP) ════════════ */
  const handleSendMessage = (e) => {
    e.preventDefault();
    const trimmed = messageText.trim();
    if (!trimmed || !activeContact || sendingMessage || sendingGroupMessage) return;

    setMessageText('');
    playOutgoingMessageTone();
    inputRef.current?.focus();

    if (activeContact.isGroup) {
      handleSendGroupMessage(trimmed, 'TEXT', null);
      return;
    }

    sendMessage({
      variables: {
        input: {
          receiverId: parseInt(activeContact.id, 10),
          message: trimmed,
          messageType: 'TEXT',
        },
      },
    });

    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !activeContact) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size should not exceed 10 MB.');
      e.target.value = '';
      return;
    }

    let messageType = 'DOCUMENT';
    if (file.type.startsWith('image/')) messageType = 'IMAGE';
    else if (file.type.startsWith('audio/')) messageType = 'AUDIO';
    else if (file.type.startsWith('video/')) messageType = 'VIDEO';

    setIsUploadingFile(true);
    const toastId = toast.loading('Uploading file...');

    try {
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('contentType', file.type || 'application/octet-stream');
      formData.append('mimeType', file.type || 'application/octet-stream');

      const response = await axiosPost(
        `${process.env.REACT_APP_REST_API_ENDPOINT}common/upload-file`,
        formData,
        {},
        auth,
      );

      const uploadedUrl = response?.data?.data;

      if (!uploadedUrl) {
        throw new Error('Failed to get upload URL from server');
      }

      toast.update(toastId, { render: 'Sending message...', type: 'info', isLoading: true });

      playOutgoingMessageTone();

      if (activeContact.isGroup) {
        handleSendGroupMessage(file.name, messageType, uploadedUrl);
      } else {
        sendMessage({
          variables: {
            input: {
              receiverId: parseInt(activeContact.id, 10),
              message: file.name,
              messageType,
              mediaUrl: uploadedUrl,
            },
          },
        });
      }

      toast.update(toastId, { render: 'File sent successfully', type: 'success', isLoading: false, autoClose: 3000 });
    } catch (error) {
      console.error('File upload error:', error);
      toast.update(toastId, { render: 'Failed to upload or send file', type: 'error', isLoading: false, autoClose: 3000 });
    } finally {
      setIsUploadingFile(false);
      e.target.value = '';
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.start();
      setIsRecordingAudio(true);
    } catch (err) {
      toast.error('Microphone access denied or not available.');
      console.error(err);
    }
  };

  const stopRecordingAndSend = () => {
    if (mediaRecorderRef.current && isRecordingAudio) {
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

        setIsUploadingFile(true);
        const toastId = toast.loading('Uploading voice message...');
        try {
          const formData = new FormData();
          formData.append('file', new File([audioBlob], 'voice_message.webm', { type: 'audio/webm' }));

          const response = await axiosPost(
            `${process.env.REACT_APP_REST_API_ENDPOINT}common/upload-file`,
            formData,
            {},
            auth,
          );

          const uploadedUrl = response?.data?.data;

          if (!uploadedUrl) {
            throw new Error('Failed to get upload URL from server');
          }

          toast.update(toastId, { render: 'Sending message...', type: 'info', isLoading: true });

          playOutgoingMessageTone();

          if (activeContact.isGroup) {
            handleSendGroupMessage('Voice Message', 'AUDIO', uploadedUrl);
          } else {
            sendMessage({
              variables: {
                input: {
                  receiverId: parseInt(activeContact.id, 10),
                  message: 'Voice Message',
                  messageType: 'AUDIO',
                  mediaUrl: uploadedUrl,
                },
              },
            });
          }

          toast.update(toastId, { render: 'Voice message sent', type: 'success', isLoading: false, autoClose: 3000 });
        } catch (error) {
          console.error('Audio upload error:', error);
          toast.update(toastId, { render: 'Failed to upload or send voice message', type: 'error', isLoading: false, autoClose: 3000 });
        } finally {
          setIsUploadingFile(false);
        }
      };

      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
      setIsRecordingAudio(false);
    }
  };

  const totalUnreadDirect = localContacts.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
  const totalUnreadGroups = groups.reduce((sum, g) => sum + (g.unreadCount || 0), 0);
  const totalUnread = totalUnreadDirect + totalUnreadGroups;

  return (
    <div className="staff-chat-page">
      <div className={`staff-chat-container mobile-view-${mobileView}`}>
        {/* Left Sidebar */}
        <div className="chat-sidebar mobile-panel">
          {/* Sidebar Header with Back & Plus (+) New Group Button */}
          <div className="chat-sidebar-header">
            <div className="user-profile">
              <button
                type="button"
                className="back-to-staff-btn"
                onClick={handleBackToStaff}
                aria-label="Back to Staff"
              >
                <ArrowLeft size={20} />
              </button>
              <div className="title">
                Internal Chat
                {totalUnread > 0 && (
                  <span className="sidebar-unread-total">{totalUnread}</span>
                )}
              </div>
            </div>

            <div className="header-actions">
              <button
                type="button"
                className="create-group-btn"
                onClick={() => setIsCreateGroupModalOpen(true)}
                title="Create New Group"
                aria-label="Create New Group"
              >
                <Plus size={20} />
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="chat-search-container">
            <div className="search-bar">
              <Search size={18} className="search-icon" />
              <input
                type="text"
                placeholder="Search by number or name..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>
          </div>

          {/* WhatsApp-Style Filter Tabs */}
          <div className="chat-filter-tabs">
            <button
              type="button"
              className={`filter-tab ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              All
            </button>
            <button
              type="button"
              className={`filter-tab ${activeTab === 'direct' ? 'active' : ''}`}
              onClick={() => setActiveTab('direct')}
            >
              Direct
            </button>
            <button
              type="button"
              className={`filter-tab ${activeTab === 'groups' ? 'active' : ''}`}
              onClick={() => setActiveTab('groups')}
            >
              Groups
              {groups.length > 0 && <span className="tab-badge">{groups.length}</span>}
            </button>
          </div>

          {/* Chat List (Direct & Groups) */}
          <div className="chat-list">
            {(() => {
              const isLoading = (loadingUsers || loadingGroups) && combinedChatList.length === 0;
              if (isLoading) {
                return (
                  <div className="chat-list-loading">
                    <Loader2 size={24} className="spin-icon" />
                    <span>Loading chats...</span>
                  </div>
                );
              }

              if (activeTab === 'groups' && loadingGroups && groups.length === 0) {
                return (
                  <div className="chat-list-loading">
                    <Loader2 size={24} className="spin-icon" />
                    <span>Loading groups...</span>
                  </div>
                );
              }

              if (usersError && groupsError && combinedChatList.length === 0) {
                return (
                  <div className="chat-list-error">
                    <span>Failed to load chats</span>
                    <button type="button" onClick={() => { refetchUsers(); refetchGroups(); }}>
                      Retry
                    </button>
                  </div>
                );
              }

              if (combinedChatList.length === 0) {
                let emptyText = 'No chats found';
                if (searchQuery) {
                  emptyText = `No results for "${searchQuery}"`;
                } else if (activeTab === 'groups') {
                  emptyText = 'No groups yet. Click the + button above to create one!';
                } else if (activeTab === 'direct') {
                  emptyText = 'No direct chats found';
                }
                return (
                  <div className="chat-list-empty">
                    {emptyText}
                  </div>
                );
              }

              return combinedChatList.map((item) => {
                const isGroup = Boolean(item.isGroup);
                const fullName = isGroup
                  ? item.name
                  : getFullName(item.firstName, item.middleName, item.lastName);
                const color = isGroup ? '#2378C9' : getAvatarColor(item.id);
                const letters = isGroup ? null : getAvatarLetters(item.firstName, item.lastName);
                const isActive = (String(activeContact?.id) === String(item.id)) ||
                  (isGroup && String(activeContact?.groupId) === String(item.groupId));
                const hasUnread = (item.unreadCount || 0) > 0;
                let itemTitle = fullName;
                if (isGroup) {
                  itemTitle = item.name;
                } else if (item.phone) {
                  itemTitle = `${fullName} (${item.phone})`;
                }

                return (
                  <div
                    key={isGroup ? `group-${item.groupId || item.id}` : `user-${item.id}`}
                    className={`chat-list-item ${isActive ? 'active' : ''} ${hasUnread ? 'has-unread' : ''}`}
                    onClick={() => handleContactClick(item)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleContactClick(item);
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="avatar-container">
                      {isGroup ? (
                        <>
                          <div
                            className="avatar-initials avatar-group-icon"
                            style={{ backgroundColor: color }}
                            aria-label={fullName}
                          >
                            <Users size={24} color="#ffffff" />
                          </div>
                          <div className="avatar-group-badge">
                            <Users size={10} color="#ffffff" />
                          </div>
                        </>
                      ) : (
                        <div
                          className="avatar-initials"
                          style={{ backgroundColor: color }}
                          aria-label={fullName}
                        >
                          {letters}
                        </div>
                      )}
                    </div>

                    <div className="chat-item-details">
                      <div className="chat-item-header">
                        <span
                          className={`chat-name ${hasUnread ? 'chat-name--unread' : ''}`}
                          title={itemTitle}
                        >
                          {fullName}
                          {!isGroup && item.phone ? ` (${item.phone})` : ''}
                          {isGroup && <span className="group-chip-tag">Group</span>}
                        </span>
                        <span className="chat-time">{formatContactTime(item.lastMessageAt || item.createdAt)}</span>
                      </div>
                      <div className="chat-item-footer">
                        <span className="chat-last-message" title={item.lastMessage || ''}>
                          {item.lastMessage || (isGroup ? (item.description || 'Group created') : 'No messages yet')}
                        </span>
                        {hasUnread && <span className="unread-badge">{item.unreadCount}</span>}
                      </div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>

        {/* Right Chat Window */}
        <div className="chat-window mobile-panel">
          {activeContact ? (
            <>
              {/* Header */}
              <div className="chat-window-header">
                <div className="chat-header-info">
                  <button
                    type="button"
                    className="mobile-back-btn"
                    onClick={handleBackToList}
                    aria-label="Back to chats"
                  >
                    <ArrowLeft size={24} />
                  </button>
                  {activeContact.isGroup ? (
                    <div
                      className="avatar-initials--header"
                      style={{ backgroundColor: '#2378C9' }}
                    >
                      <Users size={20} color="#ffffff" />
                    </div>
                  ) : (
                    <div
                      className="avatar-initials--header"
                      style={{ backgroundColor: getAvatarColor(activeContact.id) }}
                    >
                      {getAvatarLetters(activeContact.firstName, activeContact.lastName)}
                    </div>
                  )}
                  <div className="chat-header-text">
                    <div className="chat-header-name">
                      {activeContact.isGroup
                        ? activeContact.name
                        : getFullName(activeContact.firstName, activeContact.middleName, activeContact.lastName)}
                    </div>
                    {activeContact.isGroup ? (
                      <div className="chat-header-subtitle">
                        {activeContact.memberCount || activeContact.members?.length || 0} participants
                        {activeContact.members?.length > 0 && (
                          <>
                            {': '}
                            {activeContact.members
                              .map((m) => {
                                const mId = String(m.userId || m.id);
                                if (mId === String(currentUserId)) return 'You';
                                const u = localContacts.find((c) => String(c.id) === mId);
                                return u?.firstName || m.firstName || `Staff #${mId}`;
                              })
                              .slice(0, 4)
                              .join(', ')}
                            {activeContact.members.length > 4 ? '...' : ''}
                          </>
                        )}
                      </div>
                    ) : (
                      activeContact.phone && (
                        <div className="chat-header-status">{activeContact.phone}</div>
                      )
                    )}
                  </div>
                </div>

                <div className="chat-header-actions">
                  {isMessageSearchOpen ? (
                    <div className="chat-window-header-search">
                      <div className="search-input-wrapper">
                        <Search size={15} className="search-inline-icon" />
                        <input
                          ref={messageSearchInputRef}
                          type="text"
                          className="message-search-input"
                          placeholder="Search in chat..."
                          value={messageSearchTerm}
                          onChange={(e) => setMessageSearchTerm(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (e.shiftKey) handlePrevMatch();
                              else handleNextMatch();
                            } else if (e.key === 'Escape') {
                              handleCloseSearch();
                            }
                          }}
                        />
                        {messageSearchTerm && (
                          <span className="search-match-count">
                            {matchingMessageIds.length > 0
                              ? `${currentMatchIndex + 1}/${matchingMessageIds.length}`
                              : 'No match'}
                          </span>
                        )}
                      </div>
                      {matchingMessageIds.length > 0 && (
                        <div className="search-nav-buttons">
                          <button
                            type="button"
                            className="search-nav-btn"
                            onClick={handlePrevMatch}
                            title="Previous (Shift+Enter)"
                            aria-label="Previous match"
                          >
                            <ChevronUp size={16} />
                          </button>
                          <button
                            type="button"
                            className="search-nav-btn"
                            onClick={handleNextMatch}
                            title="Next (Enter)"
                            aria-label="Next match"
                          >
                            <ChevronDown size={16} />
                          </button>
                        </div>
                      )}
                      <button
                        type="button"
                        className="search-close-btn"
                        onClick={handleCloseSearch}
                        aria-label="Close search"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="header-icon-btn"
                        onClick={() => setIsMessageSearchOpen(true)}
                        title="Search messages"
                        aria-label="Search messages"
                      >
                        <Search size={20} className="icon-btn" />
                      </button>
                      {activeContact.isGroup && (
                        <button
                          type="button"
                          className="header-icon-btn"
                          onClick={() => setIsGroupInfoOpen(true)}
                          title="Group Info"
                          aria-label="Group info"
                        >
                          <Info size={20} className="icon-btn" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Messages Body */}
              <div className="chat-messages-container" ref={chatWindowRef}>
                {(loadingMessages && !activeContact.isGroup) ||
                (loadingGroupMessages && activeContact.isGroup && localMessages.length === 0) ? (
                  <div className="chat-messages-loading">
                    <Loader2 size={28} className="spin-icon" />
                    <span>Loading messages...</span>
                  </div>
                ) : (
                  <MessagesContent
                    messages={localMessages}
                    currentUserId={currentUserId}
                    highlightTerm={messageSearchTerm}
                    activeMatchId={matchingMessageIds[currentMatchIndex]}
                    isGroup={Boolean(activeContact.isGroup)}
                  />
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Chat Input */}
              <div className="chat-input-container">
                <form className="chat-input-form" onSubmit={handleSendMessage}>
                  <button
                    type="button"
                    className="attachment-btn"
                    onClick={() => fileInputRef.current?.click()}
                    aria-label="Attach file"
                    disabled={sendingMessage || sendingGroupMessage || isUploadingFile}
                    style={{ background: 'none', border: 'none', padding: '0 10px', cursor: (sendingMessage || sendingGroupMessage || isUploadingFile) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center' }}
                  >
                    {isUploadingFile ? (
                      <Loader2 size={20} className="spin-icon" color="#8696a0" />
                    ) : (
                      <Paperclip size={20} color="#8696a0" />
                    )}
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
                  />
                  <input
                    ref={inputRef}
                    type="text"
                    className="chat-input"
                    placeholder="Type a message"
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    disabled={isUploadingFile || isRecordingAudio || sendingMessage || sendingGroupMessage}
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className="attachment-btn"
                    onClick={isRecordingAudio ? stopRecordingAndSend : startRecording}
                    aria-label={isRecordingAudio ? 'Stop recording' : 'Record audio'}
                    disabled={sendingMessage || sendingGroupMessage || isUploadingFile}
                    style={{ background: 'none', border: 'none', padding: '0 10px', cursor: (sendingMessage || sendingGroupMessage || isUploadingFile) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center' }}
                  >
                    {isRecordingAudio ? (
                      <Square size={20} color="#ff3333" fill="#ff3333" />
                    ) : (
                      <Mic size={20} color="#8696a0" />
                    )}
                  </button>
                  <button
                    type="submit"
                    className="send-btn"
                    disabled={!messageText.trim() || sendingMessage || sendingGroupMessage}
                    aria-label="Send message"
                  >
                    {sendingMessage || sendingGroupMessage ? (
                      <Loader2 size={20} className="spin-icon" color="#2378C9" />
                    ) : (
                      <Send size={20} color={messageText.trim() ? '#2378C9' : '#8696a0'} />
                    )}
                  </button>
                </form>
              </div>
            </>
          ) : (
            <div className="chat-placeholder">
              <h3>Internal Chat</h3>
              <p>Select a chat or group to start messaging</p>
            </div>
          )}
        </div>
      </div>

      {/* ═══ CREATE NEW GROUP MODAL (WhatsApp Style) ═════════════════ */}
      {isCreateGroupModalOpen && (
        <div
          className="group-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsCreateGroupModalOpen(false);
            }
          }}
          role="presentation"
        >
          <div
            className="group-modal-container"
            role="dialog"
            aria-modal="true"
          >
            <div className="group-modal-header">
              <div className="modal-title-wrap">
                <Users size={20} />
                <h4>Create New Group</h4>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setIsCreateGroupModalOpen(false)}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="group-modal-body">
              {/* Group Avatar Preview */}
              <div className="group-avatar-picker">
                <div className="group-avatar-preview">
                  <Users size={32} />
                </div>
                <div className="avatar-info">
                  <div className="avatar-title">Staff Internal Group</div>
                  <div className="avatar-hint">Communicate with multiple team members together</div>
                </div>
              </div>

              {/* Group Name */}
              <div className="form-group-field">
                <label htmlFor="group-name-input">
                  Group Subject / Name <span className="req">*</span>
                </label>
                <input
                  id="group-name-input"
                  type="text"
                  placeholder="e.g. Sales Team, Operations, Core Staff"
                  value={newGroupName}
                  maxLength={50}
                  disabled={creatingGroup}
                  onChange={(e) => setNewGroupName(e.target.value)}
                />
              </div>

              {/* Group Description */}
              <div className="form-group-field">
                <label htmlFor="group-desc-input">Description (Optional)</label>
                <textarea
                  id="group-desc-input"
                  rows={2}
                  placeholder="What is the purpose of this group?"
                  value={newGroupDescription}
                  disabled={creatingGroup}
                  onChange={(e) => setNewGroupDescription(e.target.value)}
                />
              </div>

              {/* Member Selector */}
              <div className="member-select-section">
                <div className="section-header-row">
                  <span className="section-title">Add Staff Participants</span>
                  <span className="selected-count">
                    {selectedMemberIds.length} selected
                  </span>
                </div>

                {/* Selected Member Chips */}
                {selectedMemberIds.length > 0 && (
                  <div className="selected-chips-wrapper">
                    {selectedMemberIds.map((id) => {
                      const member = localContacts.find((c) => String(c.id) === String(id));
                      if (!member) return null;
                      const memberName = getFullName(member.firstName, member.middleName, member.lastName);
                      return (
                        <span key={id} className="member-chip">
                          <span
                            className="chip-avatar"
                            style={{ backgroundColor: getAvatarColor(id) }}
                          >
                            {getAvatarLetters(member.firstName, member.lastName)}
                          </span>
                          <span>{member.firstName || memberName}</span>
                          <button
                            type="button"
                            className="chip-remove"
                            disabled={creatingGroup}
                            onClick={() => setSelectedMemberIds((prev) => prev.filter((mId) => mId !== id))}
                            aria-label="Remove"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Search Member in Modal */}
                <div className="member-search-box">
                  <Search size={15} className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search staff to add..."
                    value={memberSearchQuery}
                    disabled={creatingGroup}
                    onChange={(e) => setMemberSearchQuery(e.target.value)}
                  />
                </div>

                {/* Members List */}
                <div className="members-list-scroll">
                  {loadingUsers && localContacts.length === 0 ? (
                    <div className="chat-list-loading" style={{ padding: '20px 0' }}>
                      <Loader2 size={20} className="spin-icon" />
                      <span>Loading staff...</span>
                    </div>
                  ) : (
                    localContacts
                      .filter((c) => String(c.id) !== String(currentUserId))
                      .filter((c) => {
                        const name = getFullName(c.firstName, c.middleName, c.lastName).toLowerCase();
                        const phone = (c.phone || '').toLowerCase();
                        const q = memberSearchQuery.toLowerCase().trim();
                        return name.includes(q) || phone.includes(q);
                      })
                      .map((contact) => {
                        const isChecked = selectedMemberIds.includes(String(contact.id));
                        const fullName = getFullName(contact.firstName, contact.middleName, contact.lastName);
                        const color = getAvatarColor(contact.id);
                        const letters = getAvatarLetters(contact.firstName, contact.lastName);

                        return (
                          <div
                            key={contact.id}
                            className={`member-row-item ${isChecked ? 'selected' : ''}`}
                            onClick={() => !creatingGroup && handleToggleMember(contact.id)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (!creatingGroup) handleToggleMember(contact.id);
                              }
                            }}
                          >
                            <div className="member-info-col">
                              <div className="row-avatar" style={{ backgroundColor: color }}>
                                {letters}
                              </div>
                              <div className="row-text">
                                <span className="row-name">{fullName}</span>
                                {contact.phone && <span className="row-phone">{contact.phone}</span>}
                              </div>
                            </div>
                            <div className={`custom-checkbox ${isChecked ? 'checked' : ''}`}>
                              {isChecked && <Check size={14} />}
                            </div>
                          </div>
                        );
                      })
                  )}
                </div>
              </div>
            </div>

            <div className="group-modal-footer">
              <button
                type="button"
                className="btn-secondary-custom"
                disabled={creatingGroup}
                onClick={() => setIsCreateGroupModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary-custom"
                disabled={!newGroupName.trim() || selectedMemberIds.length === 0 || creatingGroup}
                onClick={handleCreateGroupSubmit}
              >
                {creatingGroup ? (
                  <>
                    <Loader2 size={16} className="spin-icon" />
                    <span>Creating Group...</span>
                  </>
                ) : (
                  <>
                    <Users size={16} />
                    <span>Create Group</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ GROUP INFO MODAL ═════════════════════════════════════════ */}
      {isGroupInfoOpen && activeContact?.isGroup && (
        <div
          className="group-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsGroupInfoOpen(false);
            }
          }}
          role="presentation"
        >
          <div
            className="group-modal-container group-info-modal"
            role="dialog"
            aria-modal="true"
          >
            <div className="group-modal-header">
              <div className="modal-title-wrap">
                <Info size={18} />
                <h4>Group Details</h4>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setIsGroupInfoOpen(false)}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="group-modal-body">
              <div className="info-hero">
                <div className="info-avatar">
                  <Users size={40} />
                </div>
                <div className="info-title">{activeContact.name}</div>
                <div className="info-meta">
                  Group • {activeContact.memberCount || activeContact.members?.length || 0} participants
                </div>
                {activeContact.description && (
                  <div className="info-desc">{activeContact.description}</div>
                )}
              </div>

              <div className="info-members-header">
                Participants ({activeContact.memberCount || activeContact.members?.length || 0})
              </div>

              <div className="info-members-list">
                {activeContact.members?.map((member) => {
                  const { memberId, fullName, phone, letters, color } = getMemberDetails(
                    member,
                    currentUserId,
                    auth,
                    localContacts
                  );

                  return (
                    <div key={memberId} className="info-member-row">
                      <div className="info-member-left">
                        <div className="member-avatar" style={{ backgroundColor: color }}>
                          {letters}
                        </div>
                        <div className="member-names">
                          <span className="member-name">{fullName}</span>
                          {phone && <span className="member-phone">{phone}</span>}
                        </div>
                      </div>
                      {member.isAdmin && <span className="admin-pill">Admin</span>}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="group-modal-footer">
              <button
                type="button"
                className="btn-secondary-custom"
                onClick={() => setIsGroupInfoOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffChat;
